import { Button, Modal, Select } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import AtomTreeSelect from '@/components/Atoms/AtomTreeSelect/AtomTreeSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import { NULL_UUID_ID } from '@/constants'
import type { AntdTreeType, CategoryInterface } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import {
  APPROVAL_STATUS_LABELS,
  APPROVAL_STATUSES,
  type ApprovalStatus,
  getStatusBadge,
  isApprovedStatus,
} from '@/utils/catalog/approvalStatus'
import handleSelectTreeNode from '@/utils/tree/selection/handleSelectTreeNode'

import cn from '../DictionaryDetailsModal.module.scss'

type FormActionKey = '' | 'save'

type CategoryRecord = CategoryInterface['category']

type CategoryDetailsModalProps = {
  open: boolean
  item: CategoryRecord | null
  onClose: () => void
  onChanged: () => Promise<void> | void
  onLocalUpdate?: (item: CategoryRecord) => void
  categoriesTree: CategoryInterface[]
  antdCategoriesTree: AntdTreeType[]
  isApprover: boolean
  readOnly?: boolean
}

const categoryPathCache = new Map<string, string>()

const STATUS_SELECT_CLASS: Record<ApprovalStatus, string> = {
  DRAFT: cn.statusSelectDraft,
  APPROVED: cn.statusSelectApproved,
  REJECTED: cn.statusSelectRejected,
}

const normalizePathDelimiters = (path: string) => path.replace(/ > /g, ' / ')

const buildDisplayFullPath = (description: string, parentPath: string) => {
  const normalizedDescription = description.trim()
  const normalizedParentPath = normalizePathDelimiters(parentPath.trim())

  if (!normalizedParentPath) {
    return normalizedDescription
  }

  return normalizedDescription
    ? `${normalizedParentPath} / ${normalizedDescription}`
    : normalizedParentPath
}

const disableRestrictedAntdNodes = (
  nodes: AntdTreeType[],
  restrictedIds: Set<string>
): AntdTreeType[] =>
  nodes.map((node) => {
    const nodeKey = String(node.key)
    const isRestricted = restrictedIds.has(nodeKey)

    return {
      ...node,
      disabled: node.disabled || isRestricted,
      selectable: isRestricted ? false : node.selectable,
      children: disableRestrictedAntdNodes(node.children || [], restrictedIds),
    }
  })

const hasConflictError = (error: unknown) => {
  const normalizedError = error as {
    response?: {
      status?: number
      data?: {
        error?: string
        message?: string
      }
    }
  }

  return (
    normalizedError?.response?.status === 409 ||
    normalizedError?.response?.data?.error === 'CHARACTERISTICS_CONFLICT' ||
    normalizedError?.response?.data?.message?.toLowerCase().includes('conflict')
  )
}

function CategoryDetailsModal(props: CategoryDetailsModalProps) {
  const {
    open,
    item,
    onClose,
    onChanged,
    onLocalUpdate,
    categoriesTree,
    antdCategoriesTree,
    isApprover,
    readOnly = false,
  } = props

  const [description, setDescription] = useState('')
  const [parentId, setParentId] = useState('')
  const [parentPath, setParentPath] = useState('')
  const [fullPath, setFullPath] = useState('')
  const [actionKey, setActionKey] = useState<FormActionKey>('')
  const [initialDescription, setInitialDescription] = useState('')
  const [initialParentId, setInitialParentId] = useState('')
  const [status, setStatus] = useState<ApprovalStatus>('APPROVED')
  const [initialStatus, setInitialStatus] = useState<ApprovalStatus>('APPROVED')
  const [modal, contextHolder] = Modal.useModal()

  useEffect(() => {
    if (!item || !open) return

    const nextParentId = item.parentId === NULL_UUID_ID ? '' : item.parentId
    const nextStatus = item.status ?? 'APPROVED'
    setDescription(item.description)
    setParentId(nextParentId)
    setInitialDescription(item.description)
    setInitialParentId(nextParentId)
    setStatus(nextStatus)
    setInitialStatus(nextStatus)
    setParentPath(
      nextParentId
        ? handleSelectTreeNode(categoriesTree, nextParentId) || ''
        : ''
    )

    const cachedPath = categoryPathCache.get(item.id)
    setFullPath(cachedPath || '')
    api
      .get(`/categories/${item.id}/path`)
      .then((response) => {
        const pathItems = response.data.categories ?? []
        const resolvedPath = pathItems
          .map((pathItem: CategoryRecord) => pathItem.description)
          .join(' / ')
        categoryPathCache.set(item.id, resolvedPath)
        setFullPath(resolvedPath)
      })
      .catch((error) => {
        handleApiError(error, modal)
      })
  }, [item, open, categoriesTree, modal])
  const itemId = item?.id || ''
  const itemStatus = item?.status
  const itemDescription = item?.description || ''

  const isLockedApprovedCategory =
    readOnly || (!!itemStatus && isApprovedStatus(itemStatus) && !isApprover)
  const normalizedDescription = description.trim()
  const normalizedInitialDescription = initialDescription.trim()
  const isDirty =
    normalizedDescription !== normalizedInitialDescription ||
    parentId !== initialParentId
  const isStatusDirty = status !== initialStatus
  const isBusy = actionKey !== ''
  const restrictedCategoryIds = useMemo(() => new Set([itemId]), [itemId])
  const safeAntdCategoriesTree = useMemo(
    () => disableRestrictedAntdNodes(antdCategoriesTree, restrictedCategoryIds),
    [antdCategoriesTree, restrictedCategoryIds]
  )
  const draftFullPath = buildDisplayFullPath(description, parentPath)
  const displayFullPath = isDirty
    ? draftFullPath || fullPath || itemDescription
    : fullPath || draftFullPath || itemDescription

  if (!item) return null

  const persistCategory = async () => {
    await api.patch(`/categories/${item.id}`, {
      parentId: parentId || NULL_UUID_ID,
      description: normalizedDescription,
    })
    const nextPath = buildDisplayFullPath(description, parentPath)
    if (nextPath) {
      categoryPathCache.set(item.id, nextPath)
      setFullPath(nextPath)
    } else {
      categoryPathCache.delete(item.id)
      setFullPath('')
    }
  }

  const handleSave = async () => {
    if (!normalizedDescription) {
      toast.error('Заполните наименование категории')
      return
    }

    setActionKey('save')
    try {
      if (isDirty) {
        await persistCategory()
        setInitialDescription(normalizedDescription)
        setInitialParentId(parentId)
      }

      if (isStatusDirty) {
        await api.patch(`/categories/${item.id}/status`, { status })
        setInitialStatus(status)
      }

      onLocalUpdate?.({
        ...item,
        description: normalizedDescription,
        parentId: parentId || NULL_UUID_ID,
        status,
      })

      toast.success(
        isStatusDirty
          ? status === 'APPROVED'
            ? 'Категория утверждена'
            : status === 'REJECTED'
              ? 'Категория отклонена'
              : 'Категория возвращена на модерацию'
          : 'Категория обновлена'
      )

      if (!onLocalUpdate) {
        await onChanged()
      }
      onClose()
    } catch (error: unknown) {
      if (hasConflictError(error)) {
        toast.warning('Конфликт характеристик', {
          description:
            'Невозможно перенести категорию. В новой родительской ветке уже используются характеристики, которые привязаны к этой категории (или ее дочерним). Отвяжите их перед переносом.',
          duration: 3000,
        })
      } else {
        handleApiError(error, modal)
      }
    } finally {
      setActionKey('')
    }
  }

  return (
    <>
      {contextHolder}
      <Modal
        centered
        destroyOnHidden
        closable={false}
        title={<span className={cn.modalTitle}>Карточка категории</span>}
        open={open}
        footer={null}
        onCancel={onClose}
      >
        <div className={[cn.meta, cn.metaTwoColumns].join(' ')}>
          <div className={cn.metaRow}>
            <span className={cn.metaLabel}>Код:</span>
            <span>{item.code}</span>
          </div>
          <div className={cn.metaRow}>
            <span className={cn.metaLabel}>Статус:</span>
            {!readOnly && isApprover ? (
              <Select
                size="small"
                className={[cn.statusSelect, STATUS_SELECT_CLASS[status]].join(
                  ' '
                )}
                value={status}
                disabled={isBusy}
                options={APPROVAL_STATUSES.map((option) => ({
                  value: option,
                  label: APPROVAL_STATUS_LABELS[option],
                }))}
                onChange={(value) => setStatus(value)}
              />
            ) : (
              <span>{getStatusBadge(item.status) ?? 'Утверждена'}</span>
            )}
          </div>
          <div className={[cn.metaRow, cn.metaRowWide].join(' ')}>
            <span className={cn.metaLabel}>Полный путь:</span>
            <span>{displayFullPath}</span>
          </div>
        </div>

        <div className={cn.form}>
          <div className={cn.field}>
            <label>Наименование</label>
            <input
              value={description}
              readOnly={isLockedApprovedCategory}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className={cn.field}>
            <label>Родительская категория</label>
            <HoverElement>
              <AtomTreeSelect
                tree={categoriesTree}
                antdTree={safeAntdCategoriesTree}
                openByHover={false}
                value={parentId}
                disabled={isLockedApprovedCategory}
                handleChange={(tree, node) => {
                  const nextValue = typeof node === 'string' ? node : ''
                  setParentId(nextValue)
                  setParentPath(
                    nextValue ? handleSelectTreeNode(tree, nextValue) || '' : ''
                  )
                }}
                handleClear={() => {
                  setParentId('')
                  setParentPath('')
                }}
              />
            </HoverElement>
          </div>
          <div className={cn.path}>
            Выбранный родитель: {parentPath || 'нет'}
          </div>
        </div>

        <div className={cn.footer}>
          <Button className={cn.secondaryButton} onClick={onClose}>
            Отменить
          </Button>
          {!readOnly && !isLockedApprovedCategory && (
            <Button
              className={cn.primaryButton}
              disabled={(!isDirty && !isStatusDirty) || isBusy}
              loading={actionKey === 'save'}
              onClick={() => void handleSave()}
            >
              Сохранить
            </Button>
          )}
        </div>
      </Modal>
    </>
  )
}

export default CategoryDetailsModal
